<template>
  <div class="home-dark">
    <!-- Hero Carousel -->
    <section class="hero-section" v-if="carouselGames.length > 0">
      <el-carousel 
        :interval="5000" 
        arrow="always" 
        :height="isMobile ? '280px' : '400px'"
        :autoplay="true"
        class="hero-carousel"
      >
        <el-carousel-item v-for="game in carouselGames" :key="game.id">
          <div class="hero-container" @click="handleCardClick(game.id)">
            <div class="hero-image-wrapper">
              <img :src="game.cover" :alt="game.title" class="hero-image" />
              <div class="hero-gradient"></div>
            </div>
            <div class="hero-content">
              <span class="hero-badge">热门推荐 #{{ carouselGames.indexOf(game) + 1 }}</span>
              <h1 class="hero-title">{{ game.title }}</h1>
              <p class="hero-description">{{ game.content || '探索最新最热门的VR游戏体验' }}</p>
              <div class="hero-actions">
                <button class="btn-primary" @click.stop="handleCardClick(game.id)">
                  <i class="el-icon-video-play"></i>
                  立即体验
                </button>
                <button class="btn-secondary" @click.stop="handleCardClick(game.id)">
                  查看详情
                </button>
              </div>
            </div>
          </div>
        </el-carousel-item>
      </el-carousel>
    </section>
    
    <!-- Loading placeholder -->
    <section class="hero-section hero-loading" v-else>
      <div class="loading-content">
        <i class="el-icon-loading"></i>
        <p>加载热门游戏中...</p>
      </div>
    </section>

    <!-- Filters -->
    <section class="filters-section">
      <div class="filters-container">
        <button 
          v-for="filter in filters" 
          :key="filter.value"
          :class="['filter-btn', { active: searchForm.category === filter.value }]"
          @click="handleFilterChange(filter.value)"
        >
          {{ filter.label }}
        </button>
      </div>
    </section>

    <!-- Search Bar -->
    <section class="search-section">
      <div class="search-container">
        <div class="search-wrapper">
          <i class="el-icon-search search-icon"></i>
          <input 
            v-model="searchText"
            type="text" 
            placeholder="搜索游戏..." 
            class="search-input"
            @keyup.enter="handleSearch"
          />
        </div>
        <div class="download-type-tabs">
          <button 
            :class="['type-tab', { active: searchForm.download_type === '' }]"
            @click="handleTypeChange('')"
          >
            全部
          </button>
          <button 
            :class="['type-tab', { active: searchForm.download_type === 'vip' }]"
            @click="handleTypeChange('vip')"
          >
            VIP专区
          </button>
        </div>
      </div>
    </section>

    <!-- Games Grid -->
    <section class="games-section">
      <div class="section-header">
        <h2 class="section-title">
          <i class="el-icon-star-on"></i>
          热门更新
        </h2>
      </div>

      <div class="games-grid" v-loading="loading">
        <div
          v-for="item in games"
          :key="item.id"
          class="game-card"
          @click="handleCardClick(item.id)"
        >
          <div class="game-image-wrapper">
            <img :src="item.cover" :alt="item.title" class="game-image" />
            <div class="game-tags">
              <span v-if="item.download_type === 'vip'" class="tag-vip">VIP</span>
              <span v-if="isPublishedToday(item.game_update_time)" class="tag-new">NEW</span>
            </div>
            <div class="game-platform-badge">
              {{ getCategoryLabel(item.category) }}
            </div>
          </div>
          <div class="game-info">
            <h3 class="game-title">{{ item.title }}</h3>
            <p class="game-date">{{ getTheTime(item.game_update_time) }}</p>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <button 
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="handlePageChange(currentPage - 1)"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <span class="pagination-info">{{ currentPage }} / {{ totalPages }}</span>
        <button 
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="handlePageChange(currentPage + 1)"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { getMqList, getHotRanking } from "@/api";
import { formatTimeAgo } from "@/utils";

export default {
  name: "HomeDark",
  data() {
    return {
      searchText: "",
      searchForm: {
        download_type: "",
        category: "",
      },
      currentPage: 1,
      pageSize: 12,
      games: [],
      total: 0,
      loading: false,
      carouselGames: [],
      filters: [
        { label: "全部", value: "" },
        { label: "Quest", value: "quest" },
        { label: "SteamVR", value: "steamvr" },
      ],
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
    isMobile() {
      return window.innerWidth <= 768;
    },
  },

  mounted() {
    this.fetchCarouselGames();
    
    // 检查URL参数中是否有搜索关键词
    if (this.$route.query.search) {
      this.searchText = this.$route.query.search;
    }
    this.fetchGames();
  },

  beforeDestroy() {
  },

  watch: {
    // 监听路由参数变化，支持搜索功能
    '$route.query.search': {
      handler(newVal) {
        this.searchText = newVal || '';
        this.currentPage = 1;
        this.fetchGames();
      },
      immediate: false
    }
  },

  methods: {
    async fetchCarouselGames() {
      try {
        const res = await getHotRanking({
          period: 'month', // 改为本月排行
          limit: 5
        });
        console.log('轮播图数据:', res);
        // 根据API返回结构调整数据获取方式
        if (res.data && res.data.list && res.data.list.length > 0) {
          this.carouselGames = res.data.list;
        } else if (res.data && Array.isArray(res.data)) {
          this.carouselGames = res.data;
        }
        console.log('轮播图游戏列表:', this.carouselGames);
      } catch (err) {
        console.error("获取轮播图数据失败:", err);
      }
    },

    handlePageChange(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchGames();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    handleSearch() {
      this.currentPage = 1;
      this.fetchGames();
    },

    handleFilterChange(value) {
      this.searchForm.category = value;
      this.currentPage = 1;
      this.fetchGames();
    },

    handleTypeChange(value) {
      this.searchForm.download_type = value;
      this.currentPage = 1;
      this.fetchGames();
    },

    async fetchGames() {
      this.loading = true;
      try {
        const res = await getMqList({
          page: this.currentPage,
          pageSize: this.pageSize,
          title: this.searchText,
          download_type: this.searchForm.download_type,
          category: this.searchForm.category,
        });
        this.games = res.data.data;
        this.total = res.data.pagination.total;
      } catch (err) {
        console.error("获取游戏列表失败:", err);
        this.$message.error("获取游戏列表失败");
      } finally {
        this.loading = false;
      }
    },

    handleCardClick(id) {
      const routeData = this.$router.resolve({ name: "GameMoQuDetail", params: { id } });
      window.open(routeData.href, "_blank");
    },

    getCategoryLabel(category) {
      const map = {
        quest: "Quest",
        steamvr: "SteamVR",
        tutorial: "教程",
        other: "其他",
      };
      return map[category] || category;
    },

    getTheTime(time) {
      return formatTimeAgo(time);
    },

    isPublishedToday(dateString) {
      const publishDate = new Date(dateString);
      const today = new Date();
      return (
        publishDate.getFullYear() === today.getFullYear() &&
        publishDate.getMonth() === today.getMonth() &&
        publishDate.getDate() === today.getDate()
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/dark-theme.scss";

.home-dark {
  min-height: 100vh;
  background: $dark;
  padding-bottom: 60px;
  width: 100%;
  margin: 0;
  overflow-x: hidden;
}

// Hero Section
.hero-section {
  position: relative;
  height: 400px;
  margin-bottom: 32px;
  overflow: hidden;
  width: 100%;
  z-index: 1;
  touch-action: pan-x; // 只允许横向滑动

  @media (max-width: 768px) {
    height: 280px;
    margin-bottom: 24px;
  }
}

.hero-loading {
  background: $card;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .loading-content {
    text-align: center;
    color: $slate-400;
    
    i {
      font-size: 48px;
      margin-bottom: 16px;
      display: block;
    }
    
    p {
      font-size: 14px;
      margin: 0;
    }
  }
}

.hero-carousel {
  height: 100%;
  
  :deep(.el-carousel__container) {
    height: 100%;
    overflow: hidden;
  }
  
  :deep(.el-carousel__item) {
    overflow: hidden;
    height: 100%;
    touch-action: pan-x;
  }
  
  :deep(.el-carousel__arrow) {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    width: 48px;
    height: 48px;
    
    &:hover {
      background: rgba(139, 92, 246, 0.8);
    }
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      font-size: 14px;
    }
  }
  
  :deep(.el-carousel__indicators) {
    bottom: 16px;
    
    .el-carousel__indicator {
      .el-carousel__button {
        width: 30px;
        height: 4px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.3);
      }
      
      &.is-active .el-carousel__button {
        background: white;
      }
    }
    
    @media (max-width: 768px) {
      bottom: 12px;
      
      .el-carousel__indicator {
        .el-carousel__button {
          width: 20px;
          height: 3px;
        }
      }
    }
  }
}

.hero-container {
  position: relative;
  height: 100%;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  cursor: pointer;
  overflow: hidden;
  touch-action: pan-x;
}

.hero-image-wrapper {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  
  .hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .hero-gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, $dark 0%, rgba($dark, 0.7) 40%, transparent 100%);
    
    @media (max-width: 768px) {
      background: linear-gradient(to top, $dark 0%, rgba($dark, 0.85) 60%, transparent 100%);
    }
  }
}

.hero-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
    bottom: 35px;
  }
  
  > * {
    pointer-events: auto;
  }
}

.hero-badge {
  display: inline-block;
  padding: 6px 12px;
  background: rgba($primary, 0.2);
  color: $primary;
  border: 1px solid rgba($primary, 0.3);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 4px 8px;
    margin-bottom: 6px;
  }
}

.hero-title {
  font-size: 48px;
  font-weight: 700;
  color: white;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.hero-description {
  font-size: 16px;
  color: $slate-300;
  margin: 0 0 24px 0;
  max-width: 600px;
  line-height: 1.6;

  @media (max-width: 768px) {
    display: none;
  }
}

.hero-actions {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 12px;
  }
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: $transition-normal;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
    gap: 4px;
    flex: 1;
    justify-content: center;
    
    i {
      font-size: 14px;
    }
  }
}

.btn-primary {
  background: white;
  color: $dark;

  &:hover {
    background: $slate-200;
    transform: translateY(-2px);
  }
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

// Filters Section
.filters-section {
  max-width: 1400px;
  margin: 0 auto 24px;
  padding: 0 20px;
  overflow-x: auto;
  width: 100%;
  box-sizing: border-box;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

.filters-container {
  display: flex;
  gap: 12px;
  min-width: max-content;
}

.filter-btn {
  padding: 10px 20px;
  background: $card;
  color: $slate-300;
  border: $border-lighter;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: $transition-normal;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &.active {
    background: white;
    color: $dark;
    border-color: white;
  }
}

// Search Section
.search-section {
  max-width: 1400px;
  margin: 0 auto 32px;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.search-container {
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0;
  }
}

.search-wrapper {
  flex: 1;
  position: relative;

  // 移动端隐藏搜索框，因为顶部header已有搜索入口
  @media (max-width: 768px) {
    display: none;
  }
  
  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: $slate-400;
    font-size: 18px;
  }
  
  .search-input {
    width: 100%;
    padding: 12px 16px 12px 48px;
    background: $card;
    border: $border-lighter;
    border-radius: 24px;
    color: white;
    font-size: 14px;
    transition: $transition-normal;

    &::placeholder {
      color: $slate-400;
    }

    &:focus {
      outline: none;
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba($primary, 0.1);
    }
  }
}

.download-type-tabs {
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.type-tab {
  padding: 10px 20px;
  background: $card;
  color: $slate-300;
  border: $border-lighter;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: $transition-normal;

  @media (max-width: 768px) {
    flex: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &.active {
    background: $gradient-primary;
    color: white;
    border-color: transparent;
  }
}

// Games Section
.games-section {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}

.section-header {
  margin-bottom: 24px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;

  i {
    color: #f59e0b;
  }

  @media (max-width: 768px) {
    font-size: 20px;
  }
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
  }
}

.game-card {
  @include card-style;
  @include hover-lift;
  cursor: pointer;
  overflow: hidden;
}

.game-image-wrapper {
  position: relative;
  padding-top: 75%;
  overflow: hidden;
  
  .game-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  .game-tags {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 6px;
    z-index: 1;
  }
  
  .tag-vip,
  .tag-new {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
  }
  
  .tag-vip {
    background: #fbbf24;
    color: $dark;
  }
  
  .tag-new {
    background: #10b981;
    color: white;
  }
  
  .game-platform-badge {
    position: absolute;
    bottom: 8px;
    left: 8px;
    padding: 6px 12px;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(236, 72, 153, 0.9) 100%);
    backdrop-filter: blur(8px);
    color: white;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid rgba(255, 255, 255, 0.2);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
    z-index: 1;
    
    @media (max-width: 768px) {
      padding: 4px 8px;
      font-size: 10px;
    }
  }
}

.game-card:hover .game-image {
  transform: scale(1.1);
}

.game-info {
  padding: 16px;
}

.game-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin: 0 0 8px 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 44px;

  @media (max-width: 768px) {
    font-size: 14px;
    min-height: 40px;
  }
}

.game-date {
  font-size: 12px;
  color: $slate-400;
  margin: 0;
}

// Pagination
.pagination-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  background: $card;
  border: $border-lighter;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: $transition-normal;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: $primary;
    border-color: $primary;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.pagination-info {
  color: $slate-300;
  font-size: 14px;
  font-weight: 500;
}
</style>
